// @workflow assistant.approvals
/**
 * What a `client.request` can ask for and the states it goes through (contract v2). A leaf module:
 * events.js re-exports these, and the per-kind modules (clarification.js) import them from here so
 * they never import the reducer.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

/**
 * How much a write can hurt, set by the server per call (contract rule 4). Low-risk writes run
 * without asking; medium and high ask through client.request, and high gets the stronger warning.
 */
export const RISK = Object.freeze({ LOW: 'low', MEDIUM: 'medium', HIGH: 'high' });

/** What a client request asks for (contract v2 `client.request.kind`). */
export const REQUEST_KIND = Object.freeze({
  APPROVAL: 'approval',
  UI_ACTION: 'ui.action',
  UI_QUERY: 'ui.query',
  CLARIFICATION: 'clarification',
});

/** The kinds the UI runs by itself (UI tools); approvals and clarifications wait for the user. */
export const UI_REQUEST_KINDS = Object.freeze([REQUEST_KIND.UI_ACTION, REQUEST_KIND.UI_QUERY]);

/** A request's state: pending, or the `client.resolved` outcome, or unanswered when its reply ended first. */
export const REQUEST_STATUS = Object.freeze({
  PENDING: 'pending',
  APPROVED: 'approved',
  CANCELLED: 'cancelled',
  DONE: 'done',
  FAILED: 'failed',
  EXPIRED: 'expired',
  UNANSWERED: 'unanswered',
});

/** Where a pending clarification keeps its half-finished answer on the request record. */
export const DRAFT_KEY = 'steps';

/**
 * The request record with its new status. A clarification's half-finished answer (`steps`,
 * clarificationDrafts.js) lives only while the request is pending: any other status drops it.
 * @param {object} request
 * @param {string} status a REQUEST_STATUS value
 */
export function settledRequest(request, status) {
  if (status === REQUEST_STATUS.PENDING) return { ...request, status };
  const kept = Object.fromEntries(Object.entries(request).filter(([key]) => key !== DRAFT_KEY));
  return { ...kept, status };
}
